import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDialogOverlayGuard } from '../../hooks/useDialogOverlayGuard';
import type { DialogOverlayHandle } from '../../hooks/useDialogOverlayGuard';
import type { ReactNode } from 'react';
import type {
  MasterComparison,
  MasterFilterCondition,
} from '../../models/baseMaster.types';
import type { MasterColumnDef } from '../../models/masterDefinition.types';
import { useEntityDatePattern } from '@/shared/hooks/useEntityDisplayFormatOptional';
import { calendarDateFormat } from '@/shared/utils/dateUtils';

interface MasterFilterDialogProps {
  visible: boolean;
  /** What the dialog is filtering, for its title: "Organization", "Entity". */
  title: string;
  /**
   * The columns to offer a row for -- the ones the list is showing, which is
   * the set the reference builds this grid from.
   */
  columns: readonly MasterColumnDef[];
  /** The conditions currently applied. */
  conditions: readonly MasterFilterCondition[];
  onHide: () => void;
  onApply: (conditions: readonly MasterFilterCondition[]) => void;
  /**
   * Conditions of the screen's own drawn under the column rows -- the organization hierarchy of Purchase Requests and
   * the Supplier list. The
   * screen keeps their draft; Done calls `onExtraApply` and Clear All `onExtraClear`, beside the column conditions.
   */
  extra?: ReactNode;
  onExtraApply?: () => void;
  onExtraClear?: () => void;
  /** An extra class on the dialog, for a screen styling its own (Purchase Requests' design21 look). */
  className?: string;
  /** The primary button's label; "Done" when absent. */
  applyLabel?: string;
}

const COMPARISONS: { label: string; value: MasterComparison }[] = [
  { label: 'Contains', value: 'contains' },
  { label: 'Equals', value: 'equals' },
  { label: 'Starts with', value: 'startsWith' },
  { label: 'Ends with', value: 'endsWith' },
];

/* A date column's comparisons (`filterKind: 'date'`, 2026-10-09): a day, not a substring of a timestamp. */
const DATE_COMPARISONS: { label: string; value: MasterComparison }[] = [
  { label: 'Is', value: 'is' },
  { label: 'Is after', value: 'isAfter' },
  { label: 'Is before', value: 'isBefore' },
];

/* A choice column's one comparison (`filterKind: 'choice'`): the value is one of its options. */
const CHOICE_COMPARISONS: { label: string; value: MasterComparison }[] = [{ label: 'Is', value: 'equals' }];

/** What a column's row offers, and what it starts on. */
function comparisonsOf(column: MasterColumnDef) {
  if (column.filterKind === 'date') return DATE_COMPARISONS;
  if (column.filterKind === 'choice') return CHOICE_COMPARISONS;
  return COMPARISONS;
}

function defaultComparison(column: MasterColumnDef): MasterComparison {
  return comparisonsOf(column)[0]?.value ?? 'contains';
}

/** A picked day as the endpoint reads it, `yyyy-MM-dd`, in local time (no UTC shift to the previous day). */
function dayOf(date: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A stored `yyyy-MM-dd` back to the picker's local day; null when there is none. */
function dateOf(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** What one column's row holds while the dialog is open. */
interface Draft {
  comparison: MasterComparison;
  value: string;
}

function draftsFrom(
  columns: readonly MasterColumnDef[],
  conditions: readonly MasterFilterCondition[],
): Record<string, Draft> {
  const drafts: Record<string, Draft> = {};

  for (const column of columns) {
    /* Conditions are held in the endpoint's vocabulary; the row is keyed by the screen's. */
    const property = column.filterKey ?? column.key;
    const applied = conditions.find((condition) => condition.column === property);
    drafts[column.key] = {
      comparison: applied?.comparison ?? defaultComparison(column),
      value: applied?.value ?? '',
    };
  }

  return drafts;
}

/**
 * The filter builder: one row per listed column, as the reference draws it.
 *
 * Not an add-a-condition builder. The reference lays every column out at once
 * under COLUMN / CONDITION / VALUE and lets a user fill in the ones they care
 * about, which is why there is no add control, no remove control and no empty
 * first row -- a column with no value is simply not a condition.
 *
 * What it produces is unchanged: the same `MasterFilterCondition[]` the table
 * has always been given, so nothing downstream of `onApply` knows this was
 * rearranged.
 *
 * **A row appears only for a column the master's endpoint can filter on** — one declaring a
 * `filterKey`. The endpoints refuse an undeclared property with a 400 rather than ignoring it, so a row
 * for a column with nothing behind it did not fail quietly: it failed the listing, and the table went
 * empty. Most of the audit columns and every resolved label are therefore absent.
 *
 * **Status is absent too, and is not a gap.** It was offered here as a two-state dropdown, and every
 * endpoint refused it: they filter status through a separate `status` parameter, which the toolbar's
 * own Status control has always driven. That control is where status filtering lives; a second one
 * here could only ever be the broken one.
 */
export function MasterFilterDialog({
  visible,
  title,
  columns,
  conditions,
  onHide,
  onApply,
  extra,
  onExtraApply,
  onExtraClear,
  className,
  applyLabel = 'Done',
}: MasterFilterDialogProps) {
  /* The one single select (2026-10-14): every list Organization Setup's Country, searchable on the standard panel. */
  const panelClass = 'ms-panel ms-panel--std';
  const entityDatePattern = useEntityDatePattern();
  /**
   * The columns this master's endpoint can actually filter on.
   *
   * Every visible column used to get a row, including ones the endpoint has no property for — and a
   * property it does not declare is a 400 that fails the whole listing, so using the filter on one of
   * them emptied the table rather than narrowing it. Sorting has always been gated this way; this is
   * the same gate.
   */
  const filterable = useMemo(
    () => columns.filter((column) => column.filterKey !== undefined),
    [columns],
  );

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  /* Each row's condition list, closed once its row scrolls out of the dialog body. */
  const conditionSelects = useRef<Record<string, DialogOverlayHandle | null>>({});
  const overlayGuard = useDialogOverlayGuard();

  /* Reopening shows what is applied, not what was abandoned last time. */
  useEffect(() => {
    if (visible) setDrafts(draftsFrom(filterable, conditions));
  }, [conditions, filterable, visible]);

  const update = (key: string, patch: Partial<Draft>) => {
    setDrafts((previous) => ({
      ...previous,
      [key]: { comparison: 'contains', value: '', ...previous[key], ...patch },
    }));
  };

  const apply = () => {
    onApply(
      filterable
        .map((column) => ({
          /*
            The endpoint's property, not the screen's key. The two agree for most text columns and
            differ wherever the design named a column something other than the entity did — and an
            endpoint answers 400 for a property it does not declare, which fails the whole listing.
          */
          column: column.filterKey ?? column.key,
          comparison: drafts[column.key]?.comparison ?? defaultComparison(column),
          value: drafts[column.key]?.value ?? '',
        }))
        .filter((condition) => condition.value.trim() !== ''),
    );
  };

  return (
    <Dialog
      /* Scoped backdrop: the mask is portalled to <body>, so no ancestor can reach it. */
      maskClassName="ms-mask"
      visible={visible}
      modal
      draggable={false}
      resizable={false}
      className={`ms-dialog ms-filter-dialog${className ? ` ${className}` : ''}`}
      onHide={onHide}
      header={
        <div className="ms-pop__head">
          <span className="ms-pop__icon" aria-hidden="true">
            <i className="pi pi-filter" />
          </span>
          <span className="ms-pop__title">Filter {title}</span>
        </div>
      }
      footer={
        <div className="ms-filter__footer">
          <Button
            type="button"
            label="Clear All"
            outlined
            className="ms-pop__action"
            icon="pi pi-replay"
            /* Clear active conditions without closing dialog */
            onClick={() => {
              setDrafts(draftsFrom(filterable, []));
              onApply([]);
              onExtraClear?.();
            }}
          />
          <Button
            type="button"
            label={applyLabel}
            className="ms-pop__action ms-pop__action--primary"
            onClick={() => {
              apply();
              onExtraApply?.();
              onHide();
            }}
          />
        </div>
      }
    >
      <p className="ms-pop__sub">Set a condition per column. Conditions combine with AND.</p>

      <div className="ms-filter__grid">
        <span className="ms-filter__head">Column</span>
        <span className="ms-filter__head">Condition</span>
        <span className="ms-filter__head">Value</span>

        {filterable.map((column) => {
          const draft = drafts[column.key] ?? { comparison: defaultComparison(column), value: '' };

          return (
            <div className="ms-filter__row" key={column.key}>
              <span className="ms-filter__label">{column.label}</span>

              <Dropdown
                ref={(instance: DialogOverlayHandle | null) => {
                  conditionSelects.current[column.key] = instance;
                }}
                value={draft.comparison}
                options={comparisonsOf(column)}
                filter
                panelClassName={panelClass}
                aria-label={`${column.label} condition`}
                onShow={() => {
                  overlayGuard.watch(conditionSelects.current[column.key] ?? null);
                }}
                onHide={overlayGuard.release}
                onChange={(event) => {
                  update(column.key, { comparison: event.value as MasterComparison });
                }}
              />

              {column.filterKind === 'date' ? (
                /* The masters' date picker (`MasterFieldControl`'s), sent as `yyyy-MM-dd`. */
                <Calendar
                  value={dateOf(draft.value)}
                  showIcon
                  readOnlyInput
                  showButtonBar
                  panelClassName="ms-panel"
                  /* The entity's date format; the value is the day picked, `yyyy-MM-dd`. */
                  dateFormat={calendarDateFormat(entityDatePattern)}
                  placeholder="Pick a date"
                  aria-label={`${column.label} value`}
                  onChange={(event) => {
                    const picked = event.value;
                    update(column.key, { value: picked instanceof Date ? dayOf(picked) : '' });
                  }}
                />
              ) : column.filterKind === 'choice' ? (
                <Dropdown
                  value={draft.value === '' ? null : draft.value}
                  options={[...(column.filterOptions ?? [])]}
                  optionLabel="label"
                  optionValue="value"
                  showClear
                  filter
                  panelClassName={panelClass}
                  placeholder={column.filterPlaceholder ?? 'Select a value'}
                  aria-label={`${column.label} value`}
                  onChange={(event) => {
                    update(column.key, { value: typeof event.value === 'string' ? event.value : '' });
                  }}
                />
              ) : (
                <InputText
                  value={draft.value}
                  placeholder="Enter a value"
                  aria-label={`${column.label} value`}
                  onChange={(event) => {
                    update(column.key, { value: event.target.value });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      {extra}
    </Dialog>
  );
}
